import React, { useEffect, useMemo, useState } from 'react';

import type { MultiSelectOption, MultiSelectProps } from '@rtkwlf/fenrir-react';
import { HStack, MultiSelect, Primitive } from '@rtkwlf/fenrir-react';
import uniqBy from 'lodash/uniqBy';
import type { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CsvExport } from '@packages/components/csv-export/csv-export';
import { CsvUpload } from '@packages/components/csv-upload/csv-upload';
import { validateIpAddresses } from '@packages/utils/validate-ip-addresses/validate-ip-addresses';
import type { IpValidationResults } from '@packages/utils/validate-ip-addresses/validate-ip-addresses';

import { handleOptionCreate } from '../../../scanners/scanners.utils';

import { useTargetErrors } from './use-target-errors/use-target-errors';
import { useUploadMessage } from './use-upload-message/use-upload-message';

type Appearances = MultiSelectProps['appearance'];

type TargetMultiSelectProps = {
  setValue: (value: string[]) => void;
  setTargetErrors?: React.Dispatch<React.SetStateAction<FieldError[]>>;
  items: MultiSelectOption[];
  testId?: string;
  exportFileName?: string;
  validateForSchedule?: boolean;
  showCsvImportButton?: boolean;
  isDisabled?: boolean;
  isFormDirty?: boolean;
  getFieldValues?: string[];
};

export const TargetMultiSelect: React.FC<TargetMultiSelectProps> = ({
  setValue,
  items,
  testId,
  setTargetErrors,
  exportFileName,
  validateForSchedule,
  showCsvImportButton,
  isDisabled,
  isFormDirty,
  getFieldValues,
}) => {
  const { t } = useTranslation('telemetryScannerConfig');
  const [uploadMsg, setUploadMsg] = useState<boolean>(false);
  const [options, setOptions] = useState<MultiSelectOption[]>(items);
  const [selected, setSelected] = useState<MultiSelectOption[]>(items);
  const [ipValidationState, setIpValidationState] =
    useState<IpValidationResults>();
  const [fileUploadError, setFileUploadError] = useState<string>('');

  useEffect(() => {
    if (
      !isFormDirty &&
      getFieldValues &&
      getFieldValues.join(',') !== selected.map((s) => s.value).join(',')
    ) {
      setSelected(items);
      setOptions(items);
    }
  }, [getFieldValues, isFormDirty, items, selected]);

  const csvData = useMemo(() => {
    if (selected.length) {
      return `${selected.map((e) => `${e.value}`).join('\n')}`;
    }
    return '';
  }, [selected]);

  const targetErrors = useTargetErrors(ipValidationState);
  const message = useUploadMessage(ipValidationState);

  useEffect(() => {
    if (setTargetErrors && !uploadMsg) setTargetErrors(targetErrors);
  }, [setTargetErrors, targetErrors, uploadMsg]);

  const handleSelectChange = (selectedOptions: MultiSelectOption[]) => {
    const targets = selectedOptions.map((i) => i.value);
    const validationResults = validateIpAddresses(targets, {
      restrictedAddresses: validateForSchedule ? ['0.0.0.0/0'] : undefined, // NOTE: 0.0.0.0/0 is not applicable for schedules
    });
    setIpValidationState(validationResults);
    setUploadMsg(false);
    setSelected(selectedOptions);
    setValue(targets);
  };

  // set validation message for upload error
  const setUploadErrorMessage = () => {
    setUploadMsg(true);
    setFileUploadError(t('scannerConfiguration.invalidUpload'));
  };

  const handleImport = (data: string[][]) => {
    if (!data) return;

    if (data.length === 0 || data[0].length > 1) {
      setUploadErrorMessage();
      return;
    }

    const rows = data.map((r) => r[0]);

    const validationResults = validateIpAddresses(rows, {
      existingEntries: selected.map((option) => option.value),
      restrictedAddresses: ['0.0.0.0/0'], // NOTE: 0.0.0.0/0 is not applicable for schedules,
    });

    const validMultiSelectOptions = [...validationResults.valid].map((ip) => ({
      label: ip,
      value: ip,
    }));

    setOptions((prev) =>
      uniqBy([...prev, ...validMultiSelectOptions], ({ value }) => value),
    );

    const newOptions = [...selected, ...validMultiSelectOptions];
    setSelected(newOptions);
    setValue(newOptions.map((i) => i.value));

    setIpValidationState(validationResults);

    setUploadMsg(true);
    setFileUploadError('');
  };

  const placeholder = selected.length
    ? ''
    : `${options.length || 0} ${
        options.length === 1 ? t('entry') : t('entries')
      }`;

  const getAppearance = (): Appearances => {
    if (ipValidationState?.invalid.size || (uploadMsg && fileUploadError))
      return 'danger';
    if (Object.keys(ipValidationState?.duplicate || {}).length)
      return 'warning';
    return 'neutral';
  };

  return (
    <HStack yAlign="top" gap="xsmall" width="full">
      <MultiSelect
        isCreatable
        isDisabled={isDisabled}
        appearance={getAppearance()}
        useCustomValidations={!uploadMsg}
        validationMessage={fileUploadError || message}
        placeholder={placeholder}
        options={options}
        handleCreate={(newOption, updateFilterInput) =>
          handleOptionCreate(
            newOption,
            updateFilterInput,
            setOptions,
            selected,
            handleSelectChange,
          )
        }
        selectedOptions={selected}
        onSelectedOptionsChange={handleSelectChange}
      />
      {showCsvImportButton && (
        <Primitive.div data-testid="csv-import-field">
          <CsvUpload
            onFileChange={handleImport}
            testId={testId}
            isDisabled={isDisabled}
            variant="link"
            margin="small"
          />
        </Primitive.div>
      )}
      {exportFileName && (
        <Primitive.div data-testid="csv-export-field">
          <CsvExport
            data={csvData}
            fileName={exportFileName}
            isDisabled={isDisabled || !csvData || targetErrors.length > 0}
          />
        </Primitive.div>
      )}
    </HStack>
  );
};
